import { ProposalContent } from './customer-proposal.js';
import { newSelection, ProposalPricing } from './base-pricing.js';
import { carpetOrderSummary } from './carpet-layout.js';
import { withCarpetDefaults, reconcileCarpetDefaults } from './carpet-defaults.js';
import { workMeasurements } from './proposal-work-measurements.js';
/**
 * THE BINDING INVARIANT (26 Oct 2026, permanent fix part 3). An accessory row bound to a board (`bound_to`) belongs to a
 * board that is ON the Proposal. Swapping a board used to leave its old moisture barrier, underlay, trims and scotia
 * rows behind, bound to a product id nothing carried any more — Julian (J-1026-10303) had four such rows beside the
 * real ones, and 10 sat across three live Proposals on 26 October. Removing a selection now takes its bound rows with
 * it (unless another selection of the same product remains), and `save_customer_proposal` refuses a Proposal that
 * still carries one.
 */
/** The boards a Proposal carries: its priced selections and, for a room-choice Proposal (Minto), the offer's products. */
export function proposalBoardIds(doc) {
    // An offer product is known by its own id and by the catalogue product it names (`product_id`); a row may be bound to either.
    return new Set([...(doc.pricing?.products ?? []).map(p => p.productId), ...(doc.room_choice?.products ?? []).flatMap(p => [p.id, ...(p.product_id ? [p.product_id] : [])])]);
}
/** A `stair_planks:` row is the stair's own boards, folded onto the floor by the Orders list: its binding is a label, never stale. */
const neverStale = (a) => a.id.startsWith('stair_planks:');
export function staleBoundAccessories(doc) {
    const on = proposalBoardIds(doc);
    return doc.accessories.filter(a => !!a.bound_to && !on.has(a.bound_to) && !neverStale(a));
}
export function dropStaleBoundAccessories(doc) {
    const stale = new Set(staleBoundAccessories(doc).map(a => a.id));
    return stale.size ? { ...doc, accessories: doc.accessories.filter(a => !stale.has(a.id)) } : doc;
}
/** Same product insertion and package on web/native; callers supply a stable new row identity. */
export function addProposalProduct(doc, product, selectionId, catalogue) {
    const current = doc.pricing ?? ProposalPricing.parse({ products: [], services: [] });
    if (current.products.some(p => p.id === selectionId))
        throw new Error('A product selection with this identity already exists.');
    const selected = newSelection(selectionId, product.id);
    const carpet = product.pricing.unit === 'LM' && /carpet/i.test(`${product.type ?? ''} ${product.sub_type ?? ''}`);
    const layout = doc.assessment.carpet_layout;
    const order = layout && carpet ? carpetOrderSummary(layout) : null;
    const pricing = ProposalPricing.parse({ ...current, products: [...current.products, order ? { ...selected, quantity: order.wholeLm, carpetRollWidthM: layout.roll_width_m } : selected] });
    const measures = workMeasurements(doc);
    const result = carpet ? withCarpetDefaults(doc, pricing, product.id, catalogue, measures) : { ...doc, ...reconcileCarpetDefaults(doc, pricing, measures) };
    return ProposalContent.parse(result);
}
/** Remove one selection; generated carpet rows follow the same reconciliation as web edits. */
export function removeProposalProduct(doc, selectionId) {
    const current = doc.pricing;
    if (!current || current.products.filter(p => p.id === selectionId).length !== 1)
        throw new Error('The selected product line is missing or ambiguous.');
    const pricing = ProposalPricing.parse({ ...current, products: current.products.filter(p => p.id !== selectionId) });
    // The board's own accessories leave with it; a second selection of the same product keeps them.
    const next = dropStaleBoundAccessories({ ...doc, pricing });
    return ProposalContent.parse({ ...next, ...reconcileCarpetDefaults(next, pricing, workMeasurements(next)) });
}
/** Copy one commercial selection. Existing product-wide material packages remain shared. */
export function duplicateProposalProduct(doc, sourceId, selectionId) {
    const pricing = doc.pricing;
    const sources = pricing?.products.filter(p => p.id === sourceId) ?? [];
    if (!pricing || sources.length !== 1)
        throw new Error('The selected product line is missing or ambiguous.');
    if (!selectionId.trim() || selectionId.length > 80 || pricing.products.some(p => p.id === selectionId))
        throw new Error('A fresh product selection identity is required.');
    if (pricing.products.length >= 100)
        throw new Error('The Proposal already has 100 product lines.');
    const source = sources[0];
    const copied = { ...source, id: selectionId, role: 'recommended', replaces_id: undefined, group: '', rooms: [...source.rooms], scope_ids: source.scope_ids ? [...source.scope_ids] : undefined };
    return ProposalContent.parse({ ...doc, pricing: { ...pricing, products: [...pricing.products, copied] } });
}
//# sourceMappingURL=proposal-product.js.map